import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from "@UI/Components/Form/Form";
import {
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
} from "@UI/Components/Input/Input";
import theme from "@UI/theme";

import { Check } from "@UI/Components/Icons/Check";
import { CircleExclamation } from "@UI/Components/Icons/CicleExclamation";

import React, {
  FC,
  HTMLInputTypeAttribute,
  ReactNode,
} from "react";
import { UseFormRegisterReturn } from "react-hook-form";

export interface GenericInputProps {
  type?: HTMLInputTypeAttribute;
  label: string;
  id: string;
  placeholder?: string;
  registerObj: UseFormRegisterReturn;
  isError?: boolean;
  errorMessage?: string;
  leftElement?: ReactNode;
  autoComplete?: string;
  testID?: string;
  hasValidationIcons?: boolean;
  hasGreenBorder?: boolean;
  isDirty?: boolean;
  isBlurred?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
}

export const GenericInput: FC<GenericInputProps> = ({
  type = "text",
  label,
  id,
  placeholder = "",
  registerObj,
  isError = false,
  errorMessage = "",
  leftElement,
  autoComplete = "",
  testID,
  hasValidationIcons = false,
  hasGreenBorder = false,
  isDirty = false,
  isBlurred = false,
  onKeyDown,
}) => {
  const isGreen = hasGreenBorder && isDirty && isBlurred && !isError;

  let borderColor = "gray.400";
  let hoverBorderColor = "gray.700";
  let focusBorderColor = "black";

  if (isError) {
    borderColor = "red.500";
    hoverBorderColor = "red.500";
    focusBorderColor = "red.500";
  } else if (isGreen) {
    borderColor = "green.500";
    hoverBorderColor = "green.500";
    focusBorderColor = "green.500";
  }

  return (
    <FormControl isInvalid={isError} color="gray.500">
      <FormLabel color="gray.700" htmlFor={id}>
        {label}
      </FormLabel>
      <InputGroup>
        {leftElement && (
          <InputLeftElement pointerEvents="none">
            {leftElement}
          </InputLeftElement>
        )}
        <Input
          _focus={{
            borderColor: focusBorderColor,
          }}
          _hover={{
            borderColor: hoverBorderColor,
          }}
          _invalid={{
            borderColor: "red.500",
            boxShadow: "0 0 0 1px var(--chakra-colors-red-500)",
          }}
          errorBorderColor="red.500"
          borderColor={borderColor}
          autoComplete={autoComplete}
          type={type}
          {...registerObj}
          placeholder={placeholder}
          id={id}
          onKeyDown={onKeyDown}
        />
        <GenericInputRightElem
          isError={isError}
          hasGreenBorder={hasGreenBorder}
          isBlurred={isBlurred}
          isDirty={isDirty}
          hasValidationIcons={hasValidationIcons}
        />
      </InputGroup>
      {isError && (
        <FormErrorMessage data-testid={testID}>{errorMessage}</FormErrorMessage>
      )}
    </FormControl>
  );
};

interface GenericInputRightElemProps {
  isError: boolean;
  hasGreenBorder: boolean;
  isDirty: boolean;
  isBlurred: boolean;
  hasValidationIcons: boolean;
}

const GenericInputRightElem: FC<GenericInputRightElemProps> = ({
  isError,
  hasGreenBorder,
  isDirty,
  isBlurred,
  hasValidationIcons,
}) => {
  if (hasValidationIcons && isError) {
    return (
      <InputRightElement>
        <CircleExclamation color={theme.colors.red[500]} size="lg" />
      </InputRightElement>
    );
  }
  if (
    hasValidationIcons &&
    hasGreenBorder &&
    isDirty &&
    isBlurred &&
    !isError
  ) {
    return (
      <InputRightElement>
        <Check color={theme.colors.green[500]} size="lg" />
      </InputRightElement>
    );
  }
  return null;
};


